import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithTokenHandling } from '../baseQuery';
import { buildSearchParamsAuto } from '../utils/queryParams';
import { FILTER_SETS_TAG_TYPE } from './constants';

export interface GetFilterSetsParams {
  q?: string;
  serializer?: 'default' | 'detailed';
  type?: string;
}

export interface FilterSetsResponse {
  results: MedScout.FilterSet[];
  count?: number;
  next?: string | null;
  previous?: string | null;
}

export interface CreateFilterSetParams {
  data: {
    label: string;
    type: string;
    filters?: any[];
    [key: string]: any;
  };
}

export interface UpdateFilterSetParams {
  filterId: string | number;
  data: {
    label?: string;
    filters?: any[];
    [key: string]: any;
  };
}

export interface DeleteFilterSetParams {
  filterId: string | number;
}

export const filterSetsApi = createApi({
  reducerPath: 'filterSetsApi',
  baseQuery: baseQueryWithTokenHandling,
  tagTypes: [FILTER_SETS_TAG_TYPE],
  endpoints: (builder) => ({
    getFilterSets: builder.query<FilterSetsResponse, GetFilterSetsParams>({
      query: ({ q, serializer = 'detailed', type }) => {
        const params = buildSearchParamsAuto({
          q,
          serializer,
          page: 1,
          pageSize: 1000,
          type,
        });
        return `v1/accounts/filter-sets/${params ? '?' + params : ''}`;
      },
      providesTags: (result) =>
        result
          ? [
              // Tag each individual item by ID for granular invalidation
              ...result.results.map(({ id }) => ({
                type: FILTER_SETS_TAG_TYPE,
                id: id.toString(),
              })),
              // Always include LIST tag so any mutation invalidating LIST refetches all queries
              { type: FILTER_SETS_TAG_TYPE, id: 'LIST' },
            ]
          : [{ type: FILTER_SETS_TAG_TYPE, id: 'LIST' }],
    }),

    getFilterSetById: builder.query<MedScout.FilterSet, { id: string }>({
      query: ({ id }) => ({
        url: `v1/accounts/filter-sets/${id}/`,
        params: { serializer: 'detailed' },
      }),
      providesTags: (result, error, { id }) => [
        { type: FILTER_SETS_TAG_TYPE, id },
      ],
    }),

    createFilterSet: builder.mutation<MedScout.FilterSet, CreateFilterSetParams>({
      query: ({ data }) => ({
        url: 'v1/accounts/filter-sets/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: FILTER_SETS_TAG_TYPE, id: 'LIST' }],
    }),

    updateFilterSet: builder.mutation<MedScout.FilterSet, UpdateFilterSetParams>({
      query: ({ filterId, data }) => ({
        url: `v1/accounts/filter-sets/${filterId}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { filterId }) => [
        { type: FILTER_SETS_TAG_TYPE, id: 'LIST' },
        { type: FILTER_SETS_TAG_TYPE, id: filterId.toString() },
      ],
    }),

    deleteFilterSet: builder.mutation<void, DeleteFilterSetParams>({
      query: ({ filterId }) => ({
        url: `v1/accounts/filter-sets/${filterId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { filterId }) => [
        { type: FILTER_SETS_TAG_TYPE, id: 'LIST' },
        { type: FILTER_SETS_TAG_TYPE, id: filterId.toString() },
      ],
    }),
  }),
});

export const {
  useGetFilterSetsQuery,
  useGetFilterSetByIdQuery,
  useCreateFilterSetMutation,
  useUpdateFilterSetMutation,
  useDeleteFilterSetMutation,
} = filterSetsApi;
